import { ArrowBackIcon } from '@chakra-ui/icons';
import {
  Alert,
  AlertIcon,
  Box,
  Button,
  Flex,
  Link,
  Spinner,
  Text,
  useToast,
} from '@chakra-ui/react';
import React, { useEffect } from 'react';
import { Link as RouterLink } from 'react-router-dom';

import { PageLayout } from '@/components/templates/PageLayout';
// barrel 経由だとチャート系が react-virtualized の ESM build を引き込み、
// jest が変換できなくなるため直接読み込みます。
import { CmStack } from '@/components/uiParts/Layout/Stack/CmStack';

import { FileUploadSection } from './components/FileUploadSection';
import { ImportConfirmModal } from './components/ImportConfirmModal';
import { ImportPreview } from './components/ImportPreview';
import { ValidationResult } from './components/ValidationResult';
import {
  ImportFailure,
  useMosPosMenuMasterImportPage,
} from './useMosPosMenuMasterImportPage';

/** SCR009 一覧。タイトル・一覧に戻る・キャンセルの共通遷移先 */
const LIST_PATH = '/externals/pos-menu-masters';

/** 設計書 No.24 の共通エラートースト文言。5秒で自動的に消えます。 */
const TRANSIENT_ERROR_TEXT =
  'エラーが発生しました。時間をおいて再度お試しください。';

/**
 * 失敗コードごとの画面内表示。
 * NETWORK は一時的な失敗なので画面に固定表示せず、トースト（No.24）で知らせます。
 */
const failureMessage = (
  code: ImportFailure
): { status: 'error' | 'warning'; text: string } | undefined => {
  if (code === 'NETWORK') return undefined;
  switch (code) {
    case 'POS_MENU_IMPORT_ROLLED_BACK':
      return {
        status: 'error',
        text: '登録中にエラーが発生しました。データは登録前の状態にロールバックされました。内容を確認し、再度お試しください。',
      };
    case 'POS_MENU_IMPORT_RESULT_UNKNOWN':
      return {
        status: 'warning',
        text: '登録結果を確認できませんでした。再実行の前に必ず一覧で現在の状態を確認してください。',
      };
    case 'POS_MENU_IMPORT_TOO_LARGE':
      return {
        status: 'error',
        text: 'ファイルの行数またはサイズが上限を超えています。分割して再度お試しください。',
      };
    case 'POS_MENU_IMPORT_INVALID_REQUEST':
      return {
        status: 'error',
        text: 'ファイルの形式が正しくありません。CSVファイルを2つとも選択してください。',
      };
    case 'POS_MENU_SCHEMA_MIGRATION_REQUIRED':
      return {
        status: 'error',
        text: 'メニューマスタのデータ移行が完了していないため登録できません。移行の完了後に再度お試しください。',
      };
    case 'FORBIDDEN':
    case 'UNAUTHORIZED':
      return {
        status: 'error',
        text: 'この操作を行う権限がありません。',
      };
    default:
      return {
        status: 'error',
        text: '登録に失敗しました。時間をおいて再度お試しください。',
      };
  }
};

const MosPosMenuMasterImportPage: React.FC = () => {
  const {
    state,
    summary,
    findings,
    diff,
    previewMenus,
    diffByMenu,
    files,
    businessDate,
    canUse,
    canRegister,
    isValidating,
    isImporting,
    confirmOpen,
    openConfirm,
    closeConfirm,
    selectFile,
    register,
  } = useMosPosMenuMasterImportPage();

  const failure = state.errorCode ? failureMessage(state.errorCode) : undefined;

  const toast = useToast();
  useEffect(() => {
    if (state.errorCode !== 'NETWORK') return;
    toast({
      description: TRANSIENT_ERROR_TEXT,
      status: 'error',
      duration: 5000,
      isClosable: true,
    });
  }, [state.errorCode, toast]);

  return (
    <PageLayout
      title={
        <>
          <Link
            as={RouterLink}
            to={LIST_PATH}
            color="inherit"
            _hover={{ textDecoration: 'underline' }}
          >
            POSメニューマスタ
          </Link>
          ／リレーションCSVインポート
        </>
      }
    >
      <CmStack spacing={4} my={5}>
        <Flex justify="space-between" align="flex-end" gap={4} wrap="wrap">
          <Text fontSize="sm" color="gray.600" data-testid="page-description">
            CSVを取り込み、メニューマスタを登録・更新します
          </Text>
          <Button
            as={RouterLink}
            to={LIST_PATH}
            variant="ghost"
            size="sm"
            borderWidth="1px"
            borderColor="gray.300"
            leftIcon={<ArrowBackIcon data-testid="back-to-list-icon" />}
            iconSpacing={2}
          >
            一覧に戻る
          </Button>
        </Flex>

        {failure && (
          <Alert status={failure.status} data-testid="import-failure">
            <AlertIcon />
            {failure.text}
          </Alert>
        )}

        <FileUploadSection
          masterFile={state.masterFile}
          relationFile={state.relationFile}
          files={files}
          masterRows={summary?.master_rows}
          relationRows={summary?.relation_rows}
          disabled={isImporting || !canUse}
          onSelect={selectFile}
        >
          {isValidating && <Spinner data-testid="validating" />}

          {summary && !isValidating && (
            <ValidationResult
              summary={summary}
              findings={findings}
              truncated={state.validation?.findings_truncated ?? false}
            />
          )}
        </FileUploadSection>

        {diff && previewMenus.length > 0 && (
          <ImportPreview
            menus={previewMenus}
            diff={diff}
            diffByMenu={diffByMenu}
            businessDate={businessDate}
          >
            <Flex
              justify="flex-end"
              data-testid="import-actions"
              style={{
                borderTop: '1px solid #E2E8F0',
                gap: '12px',
                marginTop: '18px',
                paddingTop: '16px',
              }}
            >
              <Button
                as={RouterLink}
                to={LIST_PATH}
                variant="ghost"
                isDisabled={isImporting}
              >
                キャンセル
              </Button>
              <Button
                colorScheme="darkBlue"
                isDisabled={!canRegister || isImporting}
                isLoading={isImporting}
                data-testid="open-confirm"
                onClick={openConfirm}
              >
                この内容で登録する
              </Button>
            </Flex>
            <Text
              fontSize="xs"
              color="gray.600"
              style={{ marginTop: '6px' }}
              data-testid="concurrency-note"
            >
              ※
              排他制御は行いません。複数の担当者が同時に取り込みを行った場合、あとから登録した内容が反映されます（last
              write
              wins）。同じ対象のCSVを同時に取り込まないようご注意ください。
            </Text>
          </ImportPreview>
        )}

        {isImporting && (
          <Box
            data-testid="importing-message"
            role="status"
            aria-live="polite"
            aria-busy="true"
            style={{
              alignItems: 'center',
              background: 'rgba(255, 255, 255, 0.82)',
              display: 'flex',
              flexDirection: 'column',
              gap: '14px',
              inset: '0px',
              justifyContent: 'center',
              position: 'fixed',
              zIndex: 200,
            }}
          >
            <Spinner size="lg" color="blue.600" />
            <Text
              style={{
                background: 'white',
                border: '1px solid #E2E8F0',
                borderRadius: '4px',
                boxShadow: '0 2px 8px rgba(45,55,72,0.12)',
                fontSize: '13px',
                fontWeight: 700,
                padding: '9px 14px',
              }}
            >
              登録処理中です。しばらくお待ちください…
            </Text>
          </Box>
        )}

        {diff && (
          <ImportConfirmModal
            isOpen={confirmOpen}
            diff={diff}
            menus={previewMenus}
            isImporting={isImporting}
            onCancel={closeConfirm}
            onConfirm={() => void register()}
          />
        )}
      </CmStack>
    </PageLayout>
  );
};

export default MosPosMenuMasterImportPage;
